import React, { FunctionComponent, useState } from 'react';
import { cloneDeep, isEqual, set } from 'lodash';
import { Days, Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { OnlineStep } from '@upstart/harmony/lib/constants';

import Appointment from './Appointment';
import BusinessHours from './BusinessHours';
import './Appointment.scss';

interface Props {
  actions: {
    modifyDealerTmpStateRequest: Function;
    updateDealerRequest: Function;
  };
  dealer: Dealer;
  tmpDealer: Dealer;
  history: any;
}

export enum AppointmentType {
  Delivery = 'delivery',
  Pickup = 'pickUp',
  TestDrive = 'testDrive',
}

export enum BusinessHoursType {
  BusinessHour = 'businessHours',
}

export const appointmentTypeMap = {
  [AppointmentType.Delivery]: 'Delivery',
  [AppointmentType.Pickup]: 'Pick Up',
  [AppointmentType.TestDrive]: 'Test Drive',
};

export const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const ERROR_MESSAGE =
  'At least one of Delivery, Pick Up or Test Drive must be enabled';

export const AppointmentList: FunctionComponent<Props> = ({
  actions,
  dealer,
  tmpDealer,
  history,
}) => {
  /* The ability to enable/disable entire schedules do not exist in other applications. This will just be manually set in Chairman only */
  const [deliveryData, setDeliveryData] = useState({
    ...cloneDeep(dealer?.wizard?.delivery),
  });
  const [pickupData, setPickupData] = useState({
    ...cloneDeep(dealer?.wizard?.pickup),
  });
  const [testDriveData, setTestDriveData] = useState({
    ...cloneDeep(dealer?.wizard?.testDrive),
  });

  // in order to have the same structure as appointments so we put all the businessHours data in the days
  const [businessHoursData, setBusinessHoursData] = useState({
    days: cloneDeep(dealer?.lending?.businessHours),
  });

  const [errorMessage, setErrorMessage] = useState('');

  const onDataChange = (
    data: Dealer,
    changeType: AppointmentType | BusinessHoursType,
  ) => {
    if (!isEqual(tmpDealer, data)) {
      const updatedObj =
        changeType === BusinessHoursType.BusinessHour
          ? 'lending.businessHours'
          : `wizard.${changeType}`;
      set(tmpDealer, updatedObj, cloneDeep(data));
      actions.modifyDealerTmpStateRequest({ tmpDealer });
    }
  };

  const deliveryChangeHandler = (data: any) => {
    if (!data.enabled) {
      if (!pickupData?.enabled && !testDriveData?.enabled) {
        setErrorMessage(ERROR_MESSAGE);
        setDeliveryData(cloneDeep(deliveryData));
        return;
      }
    }

    setDeliveryData(data);
    onDataChange(data, AppointmentType.Delivery);
  };

  const pickupChangeHandler = (data: any) => {
    if (!data.enabled) {
      if (!deliveryData?.enabled && !testDriveData?.enabled) {
        setErrorMessage(ERROR_MESSAGE);
        setPickupData(cloneDeep(pickupData));
        return;
      }
    }

    setPickupData(data);
    onDataChange(data, AppointmentType.Pickup);
  };

  const testDriveChangeHandler = (data: any) => {
    if (!data.enabled) {
      if (!deliveryData?.enabled && !pickupData?.enabled) {
        setErrorMessage(ERROR_MESSAGE);
        setTestDriveData(cloneDeep(testDriveData));
        return;
      }
    }

    setTestDriveData(data);
    onDataChange(data, AppointmentType.TestDrive);
  };

  const businessHoursChangeHandler = (data: any) => {
    setBusinessHoursData(data);
    onDataChange(data.days, BusinessHoursType.BusinessHour);
  };

  const formatSchedule = (scheduleData: {
    enabled?: boolean;
    startsAfter?: string;
    endsAfter?: string;
    radius?: number;
    days?: Days;
  }) => {
    const result = cloneDeep(scheduleData);
    if (!result.enabled) {
      /* The ability to enable/disable entire schedules do not exist in other applications. This will disable all days if schedule is toggled off */
      DAYS.forEach((day) => set(result, `days.${day}`, false));
    }

    return result;
  };

  const onSave = () => {
    const newStepFlow: any = cloneDeep(dealer.stepFlowConfig);
    if (newStepFlow?.online) {
      if (!testDriveData?.enabled) {
        newStepFlow.online = newStepFlow?.online.filter(
          (step: any) => step.id !== OnlineStep.testDrive,
        );
      }
      if (!deliveryData?.enabled && !pickupData?.enabled) {
        newStepFlow.online = newStepFlow?.online.filter(
          (step: any) => step.id !== OnlineStep.schedulePickup,
        );
      }
    }

    const deliveryUpdateData = formatSchedule(deliveryData);
    const pickUpUpdateData = formatSchedule(pickupData);
    const testDriveUpdateData = formatSchedule(testDriveData);

    const dealerDataToUpdate = {
      id: dealer.id,
      wizard: {
        ...dealer.wizard,
        delivery: deliveryUpdateData,
        pickup: pickUpUpdateData,
        testDrive: testDriveUpdateData,
      },
      lending: {
        ...dealer.lending,
        businessHours: cloneDeep(businessHoursData.days),
      },
      stepFlowConfig: newStepFlow,
    };

    actions.updateDealerRequest({
      dealer: dealerDataToUpdate,
      // schedule objects need to be updates wholesale and cannot be diff'd
      // This is because the schedule objects only contain properties for
      // days that were checked in the UI, meaning a diff would only return
      // newly checked days
      diffDealerBeforeSend: false,
      route: `/${dealer.id}/appointments/`,
    });
  };

  return (
    <div className="appointments">
      <h1>Appointments</h1>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      <div className="row">
        <BusinessHours
          formData={businessHoursData}
          onChange={businessHoursChangeHandler}
        />
        <Appointment
          formData={deliveryData}
          onChange={deliveryChangeHandler}
          appointmentType={AppointmentType.Delivery}
        />
      </div>
      <div className="row">
        <Appointment
          formData={pickupData}
          onChange={pickupChangeHandler}
          appointmentType={AppointmentType.Pickup}
        />
        <Appointment
          formData={testDriveData}
          onChange={testDriveChangeHandler}
          appointmentType={AppointmentType.TestDrive}
        />
      </div>
      <div className="button-container">
        <button
          className="btn btn-primary"
          onClick={() => {
            history.push(`/dealer/${dealer.id}`);
          }}
        >
          Cancel
        </button>
        <button className="btn btn-primary" onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
};
